import type { UserSystemRoles } from "../types/enum/user-system-roles.enum";

const ALL_ROLES: ReadonlyArray<UserSystemRoles> = [
  "ADMIN",
  "TENANT_ADMIN",
  "ATTENDANT",
];
const ADMIN_AND_TENANT_ADMIN: ReadonlyArray<UserSystemRoles> = [
  "ADMIN",
  "TENANT_ADMIN",
];
const ADMIN_ONLY: ReadonlyArray<UserSystemRoles> = ["ADMIN"];

export const APP_RESOURCES = {
  PANEL_DASHBOARD: "panel.dashboard",
  PANEL_DASHBOARD_OPERATION_WIDGETS: "panel.dashboard.operation-widgets",
  PANEL_DASHBOARD_FUNNEL: "panel.dashboard.funnel",
  PANEL_CONVERSATIONS: "panel.conversations",
  PANEL_CONVERSATIONS_SUPERVISION: "panel.conversations.supervision",
  PANEL_CLIENTS: "panel.clients",
  PANEL_COMMERCIAL_FUNNEL: "panel.commercial.funnel",
  PANEL_COMMERCIAL_CAMPAIGNS: "panel.commercial.campaigns",
  PANEL_PROTOCOLS: "panel.protocols",
  PANEL_APPOINTMENTS: "panel.appointments",
  PANEL_REPORT: "panel.report",
  PANEL_CONFIG: "panel.config",
  CONFIG_TENANT: "panel.config.tenant",
  CONFIG_ESTABLISHMENT_LOCATION: "panel.config.establishment-location",
  CONFIG_SYSTEM_USER: "panel.config.system-user",
  CONFIG_SYSTEM_USER_ASSIGN_ADMIN: "panel.config.system-user.assign-admin",
  CONFIG_LOGGED_ATTENDANTS: "panel.config.logged-attendants",
  CONFIG_TENANT_CHARGE: "panel.config.tenant-charge",
  CONFIG_BLOCKED_CONTACTS: "panel.config.blocked-contacts",
  CONFIG_PREDEFINED_MESSAGES: "panel.config.predefined-messages",
  CONFIG_ATTENDANCE_WINDOW: "panel.config.attendance-window",
  CONFIG_WEEKLY_SCHEDULE: "panel.config.weekly-schedule",
  CONFIG_APPOINTMENT_REMINDERS: "panel.config.appointment-reminders",
  CONFIG_AI_TENANT: "panel.config.ai-tenant",
  CONFIG_AI_SKILLS: "panel.config.ai-skills",
  CONFIG_AI_MODELS: "panel.config.ai-models",
  CONFIG_RAG_TENANT: "panel.config.rag-tenant",
  CONFIG_TRAINING_VIDEOS: "panel.config.training-videos",
  CONFIG_PASSWORD_CHANGE: "panel.config.password-change",
  CONFIG_NOTIFICATIONS: "panel.config.notifications",
} as const;

export type AppResource = (typeof APP_RESOURCES)[keyof typeof APP_RESOURCES];

export class AccessControl {
  private static readonly RESOURCE_ROLE_MAP: Record<
    AppResource,
    ReadonlyArray<UserSystemRoles>
  > = {
    [APP_RESOURCES.PANEL_DASHBOARD]: ALL_ROLES,
    [APP_RESOURCES.PANEL_DASHBOARD_OPERATION_WIDGETS]: ADMIN_AND_TENANT_ADMIN,
    [APP_RESOURCES.PANEL_DASHBOARD_FUNNEL]: ALL_ROLES,
    [APP_RESOURCES.PANEL_CONVERSATIONS]: ALL_ROLES,
    [APP_RESOURCES.PANEL_CONVERSATIONS_SUPERVISION]: ADMIN_AND_TENANT_ADMIN,
    [APP_RESOURCES.PANEL_CLIENTS]: ALL_ROLES,
    [APP_RESOURCES.PANEL_COMMERCIAL_FUNNEL]: ALL_ROLES,
    [APP_RESOURCES.PANEL_COMMERCIAL_CAMPAIGNS]: ALL_ROLES,
    [APP_RESOURCES.PANEL_PROTOCOLS]: ALL_ROLES,
    [APP_RESOURCES.PANEL_APPOINTMENTS]: ALL_ROLES,
    [APP_RESOURCES.PANEL_REPORT]: ALL_ROLES,
    [APP_RESOURCES.PANEL_CONFIG]: ALL_ROLES,
    [APP_RESOURCES.CONFIG_TENANT]: ADMIN_ONLY,
    [APP_RESOURCES.CONFIG_ESTABLISHMENT_LOCATION]: ALL_ROLES,
    [APP_RESOURCES.CONFIG_SYSTEM_USER]: ADMIN_AND_TENANT_ADMIN,
    [APP_RESOURCES.CONFIG_SYSTEM_USER_ASSIGN_ADMIN]: ADMIN_ONLY,
    [APP_RESOURCES.CONFIG_LOGGED_ATTENDANTS]: ADMIN_ONLY,
    [APP_RESOURCES.CONFIG_TENANT_CHARGE]: ADMIN_ONLY,
    [APP_RESOURCES.CONFIG_BLOCKED_CONTACTS]: ALL_ROLES,
    [APP_RESOURCES.CONFIG_PREDEFINED_MESSAGES]: ALL_ROLES,
    [APP_RESOURCES.CONFIG_ATTENDANCE_WINDOW]: ALL_ROLES,
    [APP_RESOURCES.CONFIG_WEEKLY_SCHEDULE]: ALL_ROLES,
    [APP_RESOURCES.CONFIG_APPOINTMENT_REMINDERS]: ADMIN_ONLY,
    [APP_RESOURCES.CONFIG_AI_TENANT]: ADMIN_ONLY,
    [APP_RESOURCES.CONFIG_AI_SKILLS]: ADMIN_ONLY,
    [APP_RESOURCES.CONFIG_AI_MODELS]: ADMIN_ONLY,
    [APP_RESOURCES.CONFIG_RAG_TENANT]: ADMIN_ONLY,
    [APP_RESOURCES.CONFIG_TRAINING_VIDEOS]: ALL_ROLES,
    [APP_RESOURCES.CONFIG_PASSWORD_CHANGE]: ALL_ROLES,
    [APP_RESOURCES.CONFIG_NOTIFICATIONS]: ADMIN_ONLY,
  };

  static canView(
    resource: AppResource,
    role?: UserSystemRoles | null,
  ): boolean {
    if (!role) return false;
    return this.RESOURCE_ROLE_MAP[resource].includes(role);
  }

  static getAllowedRoles(
    resource: AppResource,
  ): ReadonlyArray<UserSystemRoles> {
    return this.RESOURCE_ROLE_MAP[resource];
  }
}
